import React, { useMemo, useRef, useTransition } from "react";
import useQueryWithErrorHandling from "utils/useQuery";
import {
  midgardClients,
  DATA_DUMP_ROWS_QUERY,
} from "providers/MidgardProvider";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useCustomerJson } from "utils/customHooks/genericHooks";
import { useDispatch } from "react-redux";
import { updateExpandedTilesDataForProcess } from "providers/GlobalStateProvider";
import { MIDGARD_EXPANDED_TILE_POLL_INTERVAL } from "constants/intervals";
import { ONE_MINUTE_OF_MS } from "utils/timeUtils";
import { useLatestSegmentForProcess } from "./expandedTileUtils";
import type { CustomerJsonType } from "types/types";
import { PnlCategoriesEnum } from "types/enums";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useInterval } from "react-use";
const DATA_DUMP_ROWS_EXPANDED_PROCESS_TILE_TITLE =
  "DATA_DUMP_ROWS_EXPANDED_PROCESS_TILE";

const ExpandedProcessTileDataGetter = ({
  processId,
}: {
  processId: string;
}) => {
  const customerJson = useCustomerJson();
  const latestSegment = useLatestSegmentForProcess(processId);
  const pnlCategory = latestSegment?.pnl.category;
  const isUncollected = pnlCategory === PnlCategoriesEnum.uncollected;

  const skip = !customerJson || isUncollected || !processId;

  return skip ? null : (
    <Query
      {...{
        skip,
        processId,
        customerJson: customerJson as CustomerJsonType,
      }}
    />
  );
};

function Query({
  skip,
  processId,
  customerJson,
}: {
  skip: boolean;
  processId: string;
  customerJson: CustomerJsonType;
}) {
  const ckey = useCustomerKey();
  const client = midgardClients.get(ckey);
  const dispatch = useDispatch();
  const now = useRef(Date.now());
  useInterval(() => {
    now.current = Date.now();
  }, 1000);

  // request range = 0s, 10min ago -- the latest dataDumpRow always has null operatorId
  const ge = String(now.current - 10 * ONE_MINUTE_OF_MS);
  const lt = String(now.current);

  const variables = useMemo(
    () => ({
      ge,
      lt,
      ckey,
      machemetryIds: [processId],
    }),
    // don't care about ge, lt since they update every second to simulate Date.now()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [processId, ckey]
  );
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();

  useQueryWithErrorHandling(
    DATA_DUMP_ROWS_EXPANDED_PROCESS_TILE_TITLE,
    DATA_DUMP_ROWS_QUERY,
    {
      client,
      pollInterval:
        MIDGARD_EXPANDED_TILE_POLL_INTERVAL * multiplyNetworkRequestIntervals,
      variables,
      skip,
      onError,
      onCompleted,
    }
  );
  const [, startTransition] = useTransition();

  function onCompleted(data?: { dataDump: any[] }) {
    if (skip || !data?.dataDump || data?.dataDump.length === 0) {
      return;
    }

    // * midgard dataDump bug
    // * the latest dataDumpRow always has null operatorId
    // * so grab the second-last row, which has the current operator id
    const dataDumpRowWithOperatorId = data.dataDump[data.dataDump.length - 2];

    if (dataDumpRowWithOperatorId) {
      const { operatorGroups } = customerJson;
      const { operatorId } = dataDumpRowWithOperatorId;
      startTransition(() => {
        dispatch(
          updateExpandedTilesDataForProcess({
            processId,
            expandedTilesDataForProcess: {
              operator: {
                id: operatorId,
                name: operatorGroups[operatorId]?.name || "",
                avatarLetters: operatorGroups[operatorId]?.avatar_letters,
                avatarLettersBg: operatorGroups[operatorId]?.avatar_letters_bg,
                avatarLettersFg: operatorGroups[operatorId]?.avatar_letters_fg,
              },
              teamSize: dataDumpRowWithOperatorId.teamSize,
            },
          })
        );
      });
    }
  }

  function onError(err: any) {
    console.log(
      `ExpandedProcessTileDataGetter data dump gone wrong: ${JSON.stringify(
        variables
      )}, ${JSON.stringify(err)}`
    );
    // useQueryWithErrorHandling will log the error + error code via handleError
  }

  return null;
}

export default React.memo(ExpandedProcessTileDataGetter);
